import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import moment from 'moment';
import { Moment } from 'moment';
import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged, map, merge } from 'rxjs';
import { YEAR_FORMATS } from 'src/app/consultant-summary/consultant-summary.component';
import { Consultant } from 'src/app/models/consultant.model';
import { Department } from 'src/app/models/department.model';
import { AuthService } from 'src/app/services/auth.service';
import { ConsultantService } from 'src/app/services/consultant.service';
import { DepartmentService } from 'src/app/services/department.service';

export declare type OrderFilter = { type: string, owner: string[], department: string[], text: string, finishedInvoicing: string, year: string };

@Component({
    selector: 'app-order-filter',
    templateUrl: './order-filter.component.html',
    styleUrls: ['./order-filter.component.scss'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'da-DK' },
        { provide: MAT_DATE_FORMATS, useValue: YEAR_FORMATS },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrderFilterComponent implements OnInit, AfterViewInit {

  @Input() consultants: Consultant[] = [];
  @Input() departments: Department[] = [];

  @Output() changed = new EventEmitter<OrderFilter>();

  projectTypesDropdown = [
    { display: "Alle", value: "ALL" },
    { display: "Projekter", value: "ORDERS" },
    { display: "Pipeline", value: "PIPELINE" },
  ];

  finishedInvoicingDropdown = [
    { display: "Alle", value: "ALL" },
    { display: "Færdig", value: "DONE" },
    { display: "Ikke færdig", value: "NOT_DONE" },
  ];

  projectOwnerDropdown = [];
  departmentDropdown = [];
  projectTypesControl = new FormControl("ALL");
  finishedInvoicingControl = new FormControl("ALL");
  projectOwnerControl = new FormControl();
  departmentControl = new FormControl();
  textControl = new FormControl();
  date = new FormControl<Date>(null, { updateOn: 'blur' });

  public lastEmitted: OrderFilter;
  private initialized = false;

  constructor(
    protected auth: AuthService,
    protected consultantsService: ConsultantService,
    protected departmentsService: DepartmentService
  ) {

  }

  ngOnInit(): void {
    this.projectOwnerDropdown = this.consultants.map(_ => ({ display: _.initials, value: _.id }));
    this.departmentDropdown = this.departments.map(_ => ({ display: _.name, value: _.id }));
    setTimeout(() => {
      const activeEmail = this.auth.activeAccount()?.username;
      const me = this.consultants.find(_ => _.email === activeEmail);
      this.projectOwnerControl.setValue(me ? [me.id] : []);
    });
    merge(
      this.projectTypesControl.valueChanges,
      this.projectOwnerControl.valueChanges,
      this.departmentControl.valueChanges,
      this.textControl.valueChanges,
      this.finishedInvoicingControl.valueChanges,
      this.date.valueChanges
    ).pipe(distinctUntilChanged(), debounceTime(500),
      map(_ => {
        return {
          type: this.projectTypesControl.value,
          owner: (this.projectOwnerControl.value ?? []).filter(Boolean),
          department: this.departmentControl.value,
          text: this.textControl.value,
          finishedInvoicing: this.finishedInvoicingControl.value,
          year: this.date.value?.getFullYear() ? this.date.value.getFullYear().toString() : ""
        };
      })
    ).subscribe(_ => {
      this.lastEmitted = _;
      if (!this.initialized) {
        // Skip the very first emission triggered by initial control setup
        this.initialized = true;
        return;
      }
      this.changed.emit(_);
    });
  }

  async setYear(normalizedMonthAndYear: Moment | any, datepicker: MatDatepicker<Date>) {
    if (!moment.isMoment(normalizedMonthAndYear) && normalizedMonthAndYear?.value) {
      normalizedMonthAndYear = moment(normalizedMonthAndYear.value, "YYYY", false);
    }

    if (!moment.isMoment(normalizedMonthAndYear)) {
      return;
    }

    setTimeout(() => {
      let year = normalizedMonthAndYear.add(12, "hours").toDate();
      this.date.setValue(year);
      if (datepicker) {
        datepicker.close();
      }
    });
  }

  ngAfterViewInit(): void {
  }

  reset() {
    setTimeout(() => {
      this.projectTypesControl.setValue("ALL");
      this.projectOwnerControl.setValue([]);
      this.departmentControl.setValue([]);
      this.textControl.setValue(null);
      this.date.setValue(null);
      this.finishedInvoicingControl.setValue("ALL");
    });
  }

  emitLast(force = false) {
    this.changed.emit(this.lastEmitted);
  }
}

